import * as React from 'react'
import clx from 'classnames'
import { Driver } from '@/app/types/creator'
import {
  SortDirection,
  TableHeaderColumn,
} from '@/modules/drivers/types/table'

import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { SimpleSpinner } from '@/app/features/company-selection/SimpleSpinner'
import { ChevronDownIcon } from '@/app/features/icons/ChevronDown'
import { Toggle } from '@/app/features/common/Toggle'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  title: string
  columns: TableHeaderColumn[]
  drivers: Driver[]
  isLoading?: boolean
  renderCell: (row: Driver, column: TableHeaderColumn) => React.ReactNode
  sortConfig: { key: string; direction: SortDirection } | null
  onHeaderClick?: (key: string) => void
  emptyLabel?: string
  defaultOpen?: boolean
  isFirst?: boolean
  selectAllChecked?: boolean
  selectAllDisabled?: boolean
  onToggleSelectAll?: (checked: boolean) => void
  selectAllLabel?: string
}

export const DriversData = ({
  title,
  columns,
  drivers,
  isLoading = false,
  renderCell,
  sortConfig,
  onHeaderClick,
  emptyLabel,
  defaultOpen = false,
  isFirst = false,
  selectAllChecked = false,
  selectAllDisabled = false,
  onToggleSelectAll,
  selectAllLabel,
}: Props) => {
  const { tSafe } = useLocalize()
  const [isOpen, setIsOpen] = React.useState(defaultOpen)
  const resolvedEmptyLabel =
    emptyLabel ?? tSafe('creator.drivers.emptyData', 'Brak danych')
  const resolvedSelectAllLabel =
    selectAllLabel ??
    tSafe('creator.drivers.selectAll', 'Zaznacz wszystkich')

  return (
    <div
      className={clx(
        'first:tw-mt-0  first:tw-rounded-t-lg tw-flex tw-flex-col tw-border-b tw-border-primary/30',
        isOpen && 'tw-min-h-0 tw-flex-1',
      )}
    >
      <div
        className={clx(
          'tw-flex tw-w-full tw-shrink-0 tw-items-center tw-bg-secondary tw-text-sm',
          isFirst && 'tw-rounded-t-lg',
        )}
      >
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          aria-expanded={isOpen}
          className="tw-flex tw-flex-1 tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-text-left hover:tw-brightness-95"
        >
          <ChevronDownIcon
            className={`tw-h-3 tw-w-3 tw-shrink-0 tw-text-content-base tw-transition-transform ${
              isOpen ? '' : '-tw-rotate-90'
            }`}
          />
          <span className="tw-font-semibold">{drivers?.length}</span>
          <span>{title}</span>
        </button>
        {onToggleSelectAll && (
          <div
            className="tw-flex tw-shrink-0 tw-flex-row tw-items-center tw-gap-2 tw-whitespace-nowrap tw-px-4 tw-py-2.5"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="tw-text-xs tw-font-medium tw-leading-none">
              {resolvedSelectAllLabel}
            </span>
            <Toggle
              checked={selectAllChecked}
              disabled={selectAllDisabled}
              onChange={onToggleSelectAll}
            />
          </div>
        )}
      </div>
      {isOpen && (
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-auto">
          <table className="tw-w-full tw-table-auto tw-border-collapse">
            <TableHeader
              sticky
              disabledCheckboxToggle
              columns={columns}
              fontSize="sm"
              sortConfig={sortConfig}
              onHeaderClick={onHeaderClick}
              maxWidthOff
            />
            <tbody className="tw-text-sm tw-text-content-base">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="tw-py-6 tw-text-center"
                  >
                    <div className="tw-flex tw-justify-center">
                      <SimpleSpinner className="tw-h-10 tw-w-10 tw-text-emerald-700" />
                    </div>
                  </td>
                </tr>
              ) : drivers.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="tw-py-3 tw-text-center"
                  >
                    {resolvedEmptyLabel}
                  </td>
                </tr>
              ) : (
                drivers.map((row) => (
                  <tr
                    key={row.id}
                    className="tw-border-b tw-border-base-300 last:tw-border-b-0"
                  >
                    {columns.map((col) => (
                      <td
                        key={col.title || col.key || ''}
                        className="tw-px-3"
                      >
                        {renderCell(row, col)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
